import React from "react";
import { ArrowUpRight, Check } from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { Reveal } from "../components/Motion";
import Seo from "../components/Seo";
import { LP_UI, LP_PRODUCTS, LP_SOON_LINE } from "../lib/liveProducts";

const ProductCard = ({ p, lang, ui }) => {
  const isLive = p.status === "live";
  const Icon = p.Icon;
  return (
    <div
      data-testid={`live-product-${p.slug}`}
      className={`group relative flex flex-col border p-8 transition-colors md:p-10 ${
        isLive ? "border-white/12 bg-white/[0.03] hover:border-champagne/40" : "border-white/8 bg-white/[0.015]"
      }`}
    >
      <div className="flex items-start justify-between">
        <span className="flex h-12 w-12 items-center justify-center border border-champagne/30 text-champagne">
          <Icon size={22} strokeWidth={1.5} />
        </span>
        <span
          className={`text-[10px] uppercase tracking-[0.25em] ${
            isLive ? "text-champagne" : "text-white/35"
          }`}
        >
          {isLive ? ui.live : ui.soon}
        </span>
      </div>

      <span className="mt-8 text-[11px] uppercase tracking-[0.3em] text-white/40">
        {p.category[lang] || p.category.de}
      </span>
      <h3 className="mt-2 font-serif text-3xl font-light text-white">{p.name}</h3>

      {isLive ? (
        <>
          <p className="mt-4 font-serif text-lg font-light italic leading-snug text-champagne/85">
            {p.tagline[lang] || p.tagline.de}
          </p>
          <p className="mt-4 text-[15px] font-light leading-relaxed text-white/65">
            {p.desc[lang] || p.desc.de}
          </p>

          {p.features && (
            <ul className="mt-6 space-y-3">
              {(p.features[lang] || p.features.de).map((f, i) => (
                <li key={i} className="flex gap-3 text-[13.5px] font-light leading-relaxed text-white/70">
                  <Check size={16} className="mt-0.5 shrink-0 text-champagne" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
          )}

          <a
            href={p.url}
            target="_blank"
            rel="noopener noreferrer"
            data-testid={`live-product-cta-${p.slug}`}
            className="mt-8 inline-flex w-fit items-center gap-2 bg-champagne px-6 py-3 text-[11px] uppercase tracking-[0.2em] text-obsidian transition-colors hover:bg-champagne-light"
          >
            {ui.visit} <ArrowUpRight size={15} />
          </a>
        </>
      ) : (
        <p className="mt-4 text-[15px] font-light leading-relaxed text-white/50">
          {LP_SOON_LINE[lang] || LP_SOON_LINE.de}
        </p>
      )}
    </div>
  );
};

const LiveProducts = () => {
  const { lang } = useLang();
  const ui = LP_UI[lang] || LP_UI.de;
  const live = LP_PRODUCTS.filter((p) => p.status === "live");
  const soon = LP_PRODUCTS.filter((p) => p.status === "soon");

  const itemListSchema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    name: ui.title,
    itemListElement: live.map((p, i) => ({
      "@type": "ListItem",
      position: i + 1,
      item: {
        "@type": "SoftwareApplication",
        name: p.name,
        applicationCategory: "LifestyleApplication",
        operatingSystem: "iOS, Android, Web",
        url: p.url,
        description: (p.desc && (p.desc[lang] || p.desc.de)) || (p.tagline && (p.tagline[lang] || p.tagline.de)),
        offers: { "@type": "Offer", price: "0", priceCurrency: "CHF" },
        publisher: { "@type": "Organization", name: "MYSYMP AG", url: "https://mysymp.ch" },
      },
    })),
  };

  return (
    <div data-testid="live-products-page" className="min-h-screen bg-obsidian">
      <Seo pageKey="live-products" schema={[itemListSchema]} />

      <section className="mx-auto max-w-[1400px] px-6 pt-40 pb-16 md:px-12 md:pt-52">
        <Reveal>
          <span className="text-xs uppercase tracking-[0.35em] text-champagne">{ui.overline}</span>
          <h1 className="mt-5 max-w-4xl font-serif text-4xl font-light leading-[1.05] text-white sm:text-5xl lg:text-6xl">
            {ui.title}
          </h1>
          <p className="mt-6 max-w-2xl text-lg font-light leading-relaxed text-white/60">{ui.sub}</p>
        </Reveal>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 pb-16 md:px-12">
        <div className="grid gap-6 md:grid-cols-2">
          {live.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.08}>
              <ProductCard p={p} lang={lang} ui={ui} />
            </Reveal>
          ))}
        </div>
      </section>

      <section className="mx-auto max-w-[1400px] px-6 pb-32 md:px-12">
        <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {soon.map((p, i) => (
            <Reveal key={p.slug} delay={i * 0.06}>
              <ProductCard p={p} lang={lang} ui={ui} />
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
};

export default LiveProducts;
